/**
 * Estimated AI processing charges: what this organisation's usage costs in
 * Azure AI services, at Microsoft's published rates.
 *
 * Deliberately separate from the plan panel. These charges are billed by
 * Microsoft to the Azure subscription that runs the app (on a Marketplace
 * install, the customer's own Azure invoice), separately from the plan fee, and
 * they leave out hosting. Useful for spotting a runaway workload.
 *
 * The per-token, four-decimal detail that dominated the old cards is real but
 * is not a headline: it is folded away until asked for.
 *
 * The period is the page-wide Analysis Period control, not a tab-local choice
 * — `costs.period` is the same `period` bucket /billing/summary computes for
 * People, scoped to whatever the control is currently set to.
 */
import { useState } from 'react'
import { ChevronDown, Info } from 'lucide-react'
import { HelpTip } from './BillingPrimitives'
import { AI_CHARGES_HELP, formatCompact, formatInt, formatUsdAs } from '../../utils/billing'
import { isAllTimeLabel } from './useBillingData'

const SERVICE_LABELS = {
  normalisation: 'Field normalisation',
  email: 'Email analysis',
  llm_fallback: 'LLM fallback extraction',
  ai_learning: 'AI learning',
}

export default function ConsumptionPanel({ costs, chart, displayCurrency, periodLabel }) {
  const [showTokens, setShowTokens] = useState(false)

  const data = costs?.period || {}
  const notes = costs?.pricing_notes || {}
  const total = data.total_estimated_usd || 0
  const docCost = data.document_processing_usd || 0
  const aiCost = data.ai_inference_usd || 0

  const docPct = total > 0 ? (docCost / total) * 100 : 0
  const aiPct = total > 0 ? (aiCost / total) * 100 : 0

  const docColor = chart.series[0]
  const aiColor = chart.series[2]
  const fmt = (usd, digits) => formatUsdAs(usd, displayCurrency, digits)

  return (
    <div className="p-5">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Estimated AI processing charges</h2>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 font-medium">
              Separate from your plan
            </span>
            <HelpTip>{AI_CHARGES_HELP}</HelpTip>
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
            Document processing plus AI inference, derived from completed jobs.
          </p>
        </div>
      </div>

      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
          {fmt(total)}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {periodLabel ? (isAllTimeLabel(periodLabel) ? 'all time' : `in the last ${periodLabel}`) : ''}
        </span>
      </div>

      {/* split of the total — two segments, labelled directly, no legend needed */}
      <div className="mt-4 flex h-2.5 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800 gap-0.5">
        <div
          className="transition-[width] duration-700 ease-out rounded-l-full"
          style={{ width: `${docPct}%`, backgroundColor: docColor }}
        />
        <div
          className="transition-[width] duration-700 ease-out rounded-r-full"
          style={{ width: `${aiPct}%`, backgroundColor: aiColor }}
        />
      </div>

      <div className="mt-4 divide-y divide-gray-100 dark:divide-gray-800">
        <div className="flex items-center gap-3 py-2.5">
          <i className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: docColor }} />
          <div className="min-w-0">
            <p className="text-[13px] font-medium text-gray-900 dark:text-gray-100">Document processing</p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
              {formatInt(data.pages_billed)} pages · Azure Content Understanding
              {(data.cu_input_tokens || 0) + (data.cu_output_tokens || 0) > 0
                && ` · ${formatCompact((data.cu_input_tokens || 0) + (data.cu_output_tokens || 0))} model tokens`}
            </p>
          </div>
          <span className="ml-auto text-[13px] font-semibold tabular-nums text-gray-900 dark:text-gray-100">
            {fmt(docCost)}
          </span>
        </div>

        <div className="py-2.5">
          <div className="flex items-center gap-3">
            <i className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: aiColor }} />
            <div className="min-w-0">
              <p className="text-[13px] font-medium text-gray-900 dark:text-gray-100">AI inference</p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                {formatCompact(data.total_tokens)} tokens · Azure OpenAI
              </p>
            </div>
            <span className="ml-auto text-[13px] font-semibold tabular-nums text-gray-900 dark:text-gray-100">
              {fmt(aiCost)}
            </span>
          </div>

          <button
            type="button"
            onClick={() => setShowTokens(v => !v)}
            aria-expanded={showTokens}
            className="mt-2 ml-[22px] inline-flex items-center gap-1 text-[11px] font-medium
                       text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200
                       transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded"
          >
            <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${showTokens ? 'rotate-180' : ''}`} />
            {showTokens ? 'Hide' : 'Show'} token detail
          </button>

          {showTokens && (
            <div className="mt-2 ml-[22px] pl-3 border-l-2 border-gray-200 dark:border-gray-700 space-y-1 animate-panel-in">
              <div className="flex justify-between text-[11px]">
                <span className="text-gray-500 dark:text-gray-400 tabular-nums">
                  Input tokens ({formatCompact(data.input_tokens)})
                </span>
                <span className="text-gray-600 dark:text-gray-300 tabular-nums">
                  {fmt(data.ai_input_cost_usd, 4)}
                </span>
              </div>
              <div className="flex justify-between text-[11px]">
                <span className="text-gray-500 dark:text-gray-400 tabular-nums">
                  Output tokens ({formatCompact(data.output_tokens)})
                </span>
                <span className="text-gray-600 dark:text-gray-300 tabular-nums">
                  {fmt(data.ai_output_cost_usd, 4)}
                </span>
              </div>
              {Object.entries(data.services || {}).map(([svc, svcData]) => (
                <div key={svc} className="flex justify-between text-[11px] pt-1 first:pt-1">
                  <span className="text-gray-500 dark:text-gray-400 tabular-nums">
                    {SERVICE_LABELS[svc] || svc} ({formatCompact(svcData.total_tokens)} tokens)
                  </span>
                  <span className="text-gray-600 dark:text-gray-300 tabular-nums">
                    {fmt(svcData.estimated_cost_usd, 4)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800 flex items-start gap-1.5">
        <Info className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" />
        <div className="text-[11px] text-gray-500 dark:text-gray-400 space-y-0.5">
          <p>{notes.document_processing}</p>
          <p>{notes.ai_inference}</p>
          <p className="italic">{notes.disclaimer}</p>
        </div>
      </div>
    </div>
  )
}
